import {
  useAppConfigContext,
  useAppConstantsContext,
} from '@ltvco/refresh-lib/ctx';
import { useFileService } from '@ltvco/refresh-lib/hooks';
import { Grid } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { ReportSection, UploadAttachmentsModal } from '@ltvco/refresh-lib/v2';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { FiberNew } from '@mui/icons-material';
import { useCallback, useState } from 'react';

import { theme } from 'theme';

import { ExploreLocalCrimePromoCard } from './propertyReportPromo/ExploreLocalCrimePromoCard';
import { ReportAttachmentsPromoCard } from './propertyReportPromo/ReportAttachmentsPromoCard';

export interface PropertyReportBusinessPromoRowProps {
  userCode: string;
  permalink: string;
  reportType?: string;
  parcelAddress: { parsed?: { zip5?: string | null } | null } | null;
  howAttachmentsCTA?: boolean;
}

export function PropertyReportBusinessPromoRow({
  userCode,
  permalink,
  reportType = 'property',
  parcelAddress,
  howAttachmentsCTA = false,
}: PropertyReportBusinessPromoRowProps) {
  const { isMedium } = useScreenSize();
  const isCompactPromoLayout = isMedium;
  const {
    images,
    data: { brandName },
  } = useAppConstantsContext();
  const { imgCloudUpload, imgCrimeWatch } = images as {
    imgBulkImport?: string;
    imgCloudUpload?: string;
    imgCrimeWatch?: string;
  };
  const {
    trackEventGA4,
    routingUtils: { navigate },
  } = useAppConfigContext();
  const queryClient = useQueryClient();
  const brand = brandName.toLowerCase();

  const { data: filesResponse, isLoading: isLoadingFiles } = useFileService({
    userCode,
    permalink,
    reportType,
  });

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const handleRefetch = useCallback(() => {
    queryClient.refetchQueries({ queryKey: ['oram-files', permalink] });
  }, [permalink, queryClient]);

  const handleUploadClick = useCallback(() => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'RA_null_click',
    });
    trackEventGA4({
      event_name: 'report_element_engagement',
      type: 'report_attachments_cta',
      interacted_from: 'property_report_new_features_row',
    });
    setIsUploadModalOpen(true);
  }, [trackEventGA4]);

  const handleCloseModal = useCallback(() => {
    setUploadFile(null);
    setIsUploadModalOpen(false);
    handleRefetch();
  }, [handleRefetch]);

  const hasNoAttachments = (filesResponse?.attachments?.length ?? 0) === 0;
  const showAttachmentsTile =
    howAttachmentsCTA && hasNoAttachments && !isLoadingFiles;

  const onSeeRecentIncidents = () => {
    const zipcode = parcelAddress?.parsed?.zip5?.trim();
    trackEventGA4({
      event_name: 'report_element_engagement',
      type: 'crime_cta',
      interacted_from: 'property_report_new_features_row',
    });
    const query = zipcode ? `?address=${encodeURIComponent(zipcode)}` : '';
    navigate(`/dashboard/neighborhood-report${query}`);
  };

  const visibleCardCount = 1 + (showAttachmentsTile ? 1 : 0);
  const mdColumns = visibleCardCount >= 2 ? 6 : 12;

  const crimeIllustration = imgCrimeWatch ? (
    <img src={imgCrimeWatch} alt="" aria-hidden />
  ) : null;
  const attachmentsIllustration = imgCloudUpload ? (
    <img src={imgCloudUpload} alt="" aria-hidden />
  ) : null;

  return (
    <>
      <ReportSection
        sectionId="new-features"
        title="New Features"
        titleIcon={
          <FiberNew
            sx={{
              fontSize: 24,
              color: theme.palette.aiChip?.main ?? theme.palette.success.main,
              mr: 0.5,
            }}
          />
        }
        sx={{ marginTop: 2.5 }}
        hasContent
      >
        <Grid
          container
          columnSpacing={isCompactPromoLayout ? 0 : 1.25}
          rowSpacing={isCompactPromoLayout ? 2.5 : 1.25}
          sx={isCompactPromoLayout ? undefined : { alignItems: 'stretch' }}
        >
          <ExploreLocalCrimePromoCard
            isCompactLayout={isCompactPromoLayout}
            mdColumns={mdColumns}
            illustration={crimeIllustration}
            onCheckIncidents={onSeeRecentIncidents}
          />

          {showAttachmentsTile ? (
            <ReportAttachmentsPromoCard
              isCompactLayout={isCompactPromoLayout}
              mdColumns={mdColumns}
              illustration={attachmentsIllustration}
              onUploadClick={handleUploadClick}
            />
          ) : null}
        </Grid>
      </ReportSection>

      <UploadAttachmentsModal
        open={isUploadModalOpen}
        onClose={handleCloseModal}
        file={uploadFile}
        userCode={userCode}
        permalink={permalink}
        brand={brand}
        reportType={reportType}
        onFileSelect={setUploadFile}
        onUploadComplete={handleRefetch}
        onFileDeleted={handleCloseModal}
        onResetUploadFile={() => setUploadFile(null)}
        entityType="Report"
      />
    </>
  );
}
